import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useParams } from 'react-router-dom';
import GoBack from 'components/actions/GoBack';
import {
    MdDoneOutline,
    MdOutlinePayment,
    MdOutlineRocketLaunch,
    MdFormatListBulleted,
} from 'react-icons/md';
import { getImage } from 'variables/images';
import InputField from 'components/fields/InputField';

const SellerMarketPlacePage = () => {
    const [user, setUser] = useState<any>(null);
    const [offer, setOffer] = useState<any>({});
    const params = useParams();
    const [finalPrice, setFinalPrice] = useState(null);
    const [error, setError] = useState(null);

    const toSellerPrice = (buyerPrice: number, fixedMargin: number) => {
        if (offer?.commissionFixedPrice != null) {
            return buyerPrice - offer.commissionFixedPrice - fixedMargin;
        }
        return (buyerPrice - fixedMargin) / (1 + Number(offer?.commissionRate));
    };

    const editPrice = (value: string) => {
        const min = Number(offer?.targetPrice.split('-')[0]);
        const max = Number(offer?.targetPrice.split('-')[1]);
        const fixedMargin = Number(offer?.fixedMargin);
        const minSellerPrice = toSellerPrice(min, fixedMargin);
        const maxSellerPrice = toSellerPrice(max, fixedMargin);
        if (Number(value) < minSellerPrice || Number(value) > maxSellerPrice) {
            setError(
                'Le prix doit etre compris entre ' +
                    minSellerPrice.toFixed(2) +
                    ' € et ' +
                    maxSellerPrice.toFixed(2) +
                    ' €'
            );
            return;
        }
        setError(null);
        setFinalPrice(value);
    };

    const displayRealPrice = (price: string, fixedMargin: number) => {
        const values = price.replace('€', '').split('-');
        const min = Number(values[0]);
        const max = Number(values[1]);
        const minSellerPrice = toSellerPrice(min, fixedMargin);
        const maxSellerPrice = toSellerPrice(max, fixedMargin);
        return `${minSellerPrice.toFixed(2)}-${maxSellerPrice.toFixed(2)}`;
    };

    const selectOffer = () => {
        if (!finalPrice) {
            setError('Veuillez entrer un prix.');
            return;
        }
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .post(
                `${CONFIG.apiUrl}/offer/${offer?._id}/select`,
                { finalPrice: finalPrice },
                config
            )
            .then(() => {
                window.location.href = '/dashboard';
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                if (user.kyc_url) {
                    setTimeout(() => {
                        window.location.href = user.kyc_url;
                    }, 3000);
                }
                setUser(res.data);
                if (res.data && res.data.type !== 'seller') {
                    window.location.href = '/my-request';
                }
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const getOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/marketplace/${id}`, config)
            .then((res) => {
                setOffer(res.data);
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token').length < 10
        )
            window.location.href = '/auth/sign-up';
        getOffer(params.id);
        initUser();
        // eslint-disable-next-line
    }, []);

    const InfoBox = ({ icon, title, description }: { icon?: any; title?: string; description?: string }) => (
        <div className="w-full rounded-xl border border-leadrs-stroke bg-leadrs-bg p-4">
            <div className="flex items-start gap-3">
                <span className="mt-0.5 text-leadrs-text-soft">{icon}</span>
                <div>
                    <h4 className="font-geist text-sm font-bold text-leadrs-text">{title}</h4>
                    <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{description}</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <GoBack />

            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="flex gap-8">
                    <div className="flex-shrink-0">
                        {getImage(offer?.industry, offer?.subIndustry) ? (
                            <img
                                src={getImage(offer?.industry, offer?.subIndustry)}
                                className="mb-1 max-h-[400px] w-full rounded-xl object-cover"
                                alt={`${offer?.industry} ${offer?.subIndustry}`}
                            />
                        ) : (
                            <div className="flex h-[400px] w-[400px] items-center justify-center rounded-xl bg-leadrs-bg">
                                <p className="font-geist text-sm text-leadrs-text-soft">
                                    {offer?.industry} - {offer?.subIndustry}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex-1">
                        <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                            {offer?.industry}
                        </p>
                        <h1 className="mt-1 font-crimson text-3xl font-semibold text-leadrs-text">
                            {offer?.subIndustry}
                        </h1>
                        <p className="mb-4 font-geist text-sm font-semibold text-leadrs-text-soft">
                            #{offer?.offerNumber}
                        </p>

                        <div className="mb-4 h-px w-full bg-leadrs-stroke" />

                        <div className="flex flex-col gap-3">
                            {offer?.status === 'done' && (
                                <InfoBox
                                    icon={<MdDoneOutline size={20} />}
                                    title="Mission terminee"
                                    description={`Vous avez genere ${offer?.leadsGenerated} (${(offer?.leadsGenerated * offer?.sellerPrice).toFixed(2)}€) leads. Profitez de cette traction et cherchez un autre acheteur avec le meme besoin`}
                                />
                            )}
                            {offer?.status === 'running' && (
                                <InfoBox
                                    icon={<MdOutlineRocketLaunch size={20} />}
                                    title="Mission en cours"
                                    description={`Vous avez deja genere ${(offer?.leadsGenerated * offer?.sellerPrice).toFixed(2)}€ (${offer?.leadsGenerated} leads) pour cette mission. Le payement est effectue chaque semaine sur votre compte bancaire.`}
                                />
                            )}
                            {offer?.status === 'waiting_payment' && (
                                <InfoBox
                                    icon={<MdOutlinePayment size={20} />}
                                    title="En attente de paiement"
                                    description="Vous pourrez commencer votre campagne une fois la commande payee par l'acheteur. En attendant, si un lead est genere, nous nous assurons de vous le rediriger par mail."
                                />
                            )}
                            {!user?.isKYCVerified && (
                                <InfoBox
                                    icon={<MdOutlinePayment size={20} />}
                                    title="Coordonnees bancaires manquantes"
                                    description="Avant de vous positionner sur une offre, vous devez renseigner vos coordonnees bancaires pour recevoir vos paiements."
                                />
                            )}
                            {!user?.isValidated && (
                                <p className="font-geist text-sm text-leadrs-red-focus">
                                    Nous sommes en train de verifier votre compte. Vous ne pouvez pas encore postuler a une offre.
                                </p>
                            )}
                            {offer?.status === 'on_market' && user?.isKYCVerified && user?.isValidated && (
                                <div className="rounded-xl bg-leadrs-blue-soft px-4 py-4">
                                    <InputField
                                        label="Prix final"
                                        placeholder={
                                            displayRealPrice(offer?.targetPrice, offer?.fixedMargin).split('-')[1]
                                        }
                                        id="finalPrice"
                                        type="number"
                                        onChange={(e: any) => editPrice(e.target.value)}
                                    />
                                    {error && (
                                        <p className="mb-3 font-geist text-sm text-leadrs-red-focus">{error}</p>
                                    )}
                                    <p className="mb-3 font-geist text-sm text-leadrs-text-soft">
                                        Remuneration totale = {offer?.weeklyQuantity} leads * {finalPrice}€ = {finalPrice * offer?.weeklyQuantity}€
                                    </p>
                                    <button
                                        className="w-full flex items-center justify-center gap-2 rounded-xl bg-leadrs-yellow-soft px-3 py-2.5 font-geist text-sm font-medium text-leadrs-yellow-focus hover:opacity-80 transition-opacity"
                                        onClick={selectOffer}
                                    >
                                        <MdFormatListBulleted className="h-4 w-4 flex-shrink-0" />
                                        Vendre {displayRealPrice(offer?.targetPrice, offer?.fixedMargin)}€ par lead
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="mt-5 grid grid-cols-1 gap-4">
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Quantite</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.weeklyQuantity} leads</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Champs de donnees</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.fields?.join(', ')}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Canaux d'acquisition</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.channels?.join(', ')}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Besoin</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.type}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Site web</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.websiteInfo}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Description</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.description}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Departements</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.regions?.join(', ')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellerMarketPlacePage;
